/**
 * tenant-admin.ts
 * Shared Zod contracts for tenant-admin user and role management — KI-17
 *
 * Covers:
 *  - TenantUser: the DTO returned by GET /admin/identity/users
 *  - TenantUserListResponse: paginated list response (reuses buildPaginatedResponse<TenantUser>)
 *  - CreateTenantUserBody: POST /admin/identity/users request body (NO companyId — KI-14.2)
 *  - PatchTenantUserBody: PATCH /admin/identity/users/:id active toggle
 *  - PatchTenantUserProfileBody: PATCH /admin/identity/users/:id profile fields
 *  - TenantRole: the DTO returned by GET /admin/identity/roles
 *  - TenantRoleListResponse: response for role listing
 *  - AssignRoleBody: POST /admin/identity/users/:id/roles request body
 *
 * ZERO Hono/Drizzle/infrastructure imports — Zod only.
 *
 * Spec: KI-14, KI-15, KI-16, KI-17, KI-18
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// TenantUser — returned by GET /admin/identity/users (KI-14)
// ---------------------------------------------------------------------------

export const TenantUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  active: z.boolean(),
  /** ISO 8601 date string — backend serialises createdAt as toISOString() */
  createdAt: z.string(),
  /** Role names embedded from the user_role JOIN (design §3) */
  roles: z.array(z.string()),
  /**
   * User profile fields — nullable (not yet filled for existing users).
   * Using .optional() for additive compatibility: backend always provides
   * these as null | string; frontend can treat them as potentially absent.
   */
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  sector: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  /**
   * False when the user holds a capability the viewer lacks: the backend
   * rejects editing, deactivating or resetting their password (anti-escalation).
   * Sent only by the list endpoint; absent means manageable.
   */
  manageable: z.boolean().optional(),
});

export type TenantUser = z.infer<typeof TenantUserSchema>;

// ---------------------------------------------------------------------------
// CreateTenantUserBody — POST /admin/identity/users (KI-14, KI-17.2)
//
// CRITICAL: NO companyId field. The backend always reads companyId from
// TenantContext (KC-3.5). Any companyId present in the request body is
// stripped silently by Zod's default strip mode.
// ---------------------------------------------------------------------------

export const CreateTenantUserBodySchema = z.object({
  email: z.string().email('email must be a valid email address'),
  /**
   * Required when the company signs in with a password; must be absent when it
   * signs in with Microsoft/Google (the backend checks company.sign_in_method).
   */
  password: z.string().min(8, 'password must be at least 8 characters').optional(),
  // Optional profile fields — stored on app_user at creation time
  firstName: z.string().trim().max(100).optional(),
  lastName: z.string().trim().max(100).optional(),
  sector: z.string().trim().max(100).optional(),
  phone: z.string().trim().max(50).optional(),
  // NOTE: companyId is intentionally absent. TenantContext provides it.
});

export type CreateTenantUserBody = z.infer<typeof CreateTenantUserBodySchema>;

// ---------------------------------------------------------------------------
// CreateTenantUserResponse — returned on 201 (KI-14)
// ---------------------------------------------------------------------------

export const CreateTenantUserResponseSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  active: z.boolean(),
  createdAt: z.string(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  sector: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
});

export type CreateTenantUserResponse = z.infer<typeof CreateTenantUserResponseSchema>;

// ---------------------------------------------------------------------------
// PatchTenantUserBody — PATCH /admin/identity/users/:id active toggle (KI-14)
// ---------------------------------------------------------------------------

export const PatchTenantUserBodySchema = z.object({
  active: z.boolean({
    required_error: 'active is required',
    invalid_type_error: 'active must be a boolean',
  }),
});

export type PatchTenantUserBody = z.infer<typeof PatchTenantUserBodySchema>;

// ---------------------------------------------------------------------------
// PatchTenantUserProfileBody — PATCH /admin/identity/users/:id profile fields
//
// Used when the request body does NOT contain the `active` key.
// At least one field must be provided (validated in the route handler).
// ---------------------------------------------------------------------------

export const PatchTenantUserProfileBodySchema = z.object({
  firstName: z.string().trim().max(100).optional(),
  lastName: z.string().trim().max(100).optional(),
  sector: z.string().trim().max(100).optional(),
  phone: z.string().trim().max(50).optional(),
});

export type PatchTenantUserProfileBody = z.infer<typeof PatchTenantUserProfileBodySchema>;

// ---------------------------------------------------------------------------
// TenantRole — returned by GET /admin/identity/roles (KI-16)
// ---------------------------------------------------------------------------

export const TenantRoleSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  permissions: z.array(z.string()),
  /** RigX template: read-only, can be duplicated into a custom role. */
  isBuiltIn: z.boolean(),
  /** The caller holds every capability of this role (anti-escalation): may assign it, edit or delete it. */
  manageable: z.boolean(),
  /** Users of this company holding the role. */
  userCount: z.number().int().nonnegative(),
});

export type TenantRole = z.infer<typeof TenantRoleSchema>;

// ---------------------------------------------------------------------------
// TenantRoleListResponse — GET /admin/identity/roles response (KI-16)
// ---------------------------------------------------------------------------

export const TenantRoleListResponseSchema = z.object({
  roles: z.array(TenantRoleSchema),
});

export type TenantRoleListResponse = z.infer<typeof TenantRoleListResponseSchema>;

// ---------------------------------------------------------------------------
// AssignRoleBody — POST /admin/identity/users/:id/roles (KI-16)
// ---------------------------------------------------------------------------

export const AssignRoleBodySchema = z.object({
  roleId: z.string().uuid('roleId must be a valid UUID'),
});

export type AssignRoleBody = z.infer<typeof AssignRoleBodySchema>;

// ---------------------------------------------------------------------------
// ResetPasswordResponse — POST /admin/identity/users/:id/reset-password
// (2026-07-21, admin-triggered blanqueo — no self-service flow exists yet)
// ---------------------------------------------------------------------------

export const ResetPasswordResponseSchema = z.object({
  /** Shown ONCE — never stored, never logged, never emailed by this endpoint. */
  tempPassword: z.string(),
  /**
   * Other ACTIVE memberships (companies) sharing this same account, excluding
   * this one — the credential lives on identity.account, shared across every
   * company that account logs into. 0 = this reset affects only this tenant.
   */
  affectedMemberships: z.number().int().nonnegative(),
});

export type ResetPasswordResponse = z.infer<typeof ResetPasswordResponseSchema>;

// ---------------------------------------------------------------------------
// Tenant role management (permisos-por-capability stage 3, 2026-10-14)
// POST/PATCH /admin/identity/roles reuse CreateRoleBodySchema/UpdateRoleBodySchema
// (platform-role.ts) — same name + capability-key rules as the platform console.
// ---------------------------------------------------------------------------

/** GET /admin/identity/users/:id/access — what a user can do, and which role grants each key. */
export const TenantUserAccessSchema = z.object({
  roles: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  capabilities: z.array(
    z.object({
      key: z.string(),
      /** Role names granting it; 'grant' = an individual permission given from the platform console. */
      sources: z.array(z.string()),
    }),
  ),
});

export type TenantUserAccess = z.infer<typeof TenantUserAccessSchema>;

export const ACCESS_AUDIT_ACTIONS = [
  'role.create',
  'role.update',
  'role.delete',
  'user.role.assign',
  'user.role.revoke',
  'user.activate',
  'user.deactivate',
] as const;

export const AccessAuditActionSchema = z.enum(ACCESS_AUDIT_ACTIONS);
export type AccessAuditAction = z.infer<typeof AccessAuditActionSchema>;

/** One row of GET /admin/identity/access-audit (paginated, newest first). */
export const AccessAuditEntrySchema = z.object({
  id: z.string().uuid(),
  actorLabel: z.string(),
  action: AccessAuditActionSchema,
  targetLabel: z.string(),
  /** role.*: { name?, added?, removed? }; user.role.*: { role }. */
  detail: z.record(z.unknown()),
  createdAt: z.string(),
});

export type AccessAuditEntry = z.infer<typeof AccessAuditEntrySchema>;
